import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3, Users, TrendingUp, Minus, Shield, AlertTriangle,
  ChevronRight, ArrowUpRight, ArrowDownRight, Activity, Target, Clock,
  MessageSquare, CheckCircle2, FileText, Compass, ListTodo, Scale,
  Gavel, Layers, Zap, Calendar, Star, CircleDot,
} from 'lucide-react';
import { VUKZ, VUKZ_GRADIENT } from '../theme/vukzTheme';
import { isRouteOutOfScope } from '../config/demoScope';
import VukzAssistPanel from '../components/assist/VukzAssistPanel';
import PageLayout from '../components/layout/PageLayout';
import QuotaSuspensionAlert from '../components/manager/QuotaSuspensionAlert';
import UsageWidget from '../components/usage/UsageWidget';
import { getClubUsage } from '../services/usageClubService';
import { useSportsLang } from '../contexts/SportsLangContext';
import { useActiveContext } from '../contexts/ActiveContextContext';
import {
  getCategoriesSummary, getMethodology, getAlerts, getTrends, getInterventions,
  getManagerKpis,
  SEVERITY_COLORS,
} from '../services/managerPerformanceService';
import {
  getGestorDashboard, getPlanDirectives, getPlanDecisions, getPlanTasks,
  getPlanExceptions, PHASE_COLORS, AGENT_LABELS, DIRECTIVE_STATUS,
  DECISION_TYPE_LABELS, DECISION_TYPE_COLORS,
} from '../services/gestorService';

// ═══════════════════════════════════════════════════════════════════════════════
// i18n
// ═══════════════════════════════════════════════════════════════════════════════
const LABELS = {
  pt: {
    pageTitle: 'Gestor Estratégico',
    pageSubtitle: 'Planejamento macro · Diretrizes por categoria · Governança de metodologia',
    viewPlan: 'Ver Plano',
    // Active plan banner
    active: 'ATIVO',
    viewFullPlan: 'Ver Plano Completo',
    macrocyclePhasesLabel: 'Fases do Macrociclo',
    currentLabel: '← ATUAL',
    progressLabel: 'Progresso',
    weekLabel: (elapsed, total) => `Sem ${elapsed}/${total}`,
    northStar: 'North Star',
    alignmentScore: 'Alignment Score',
    directives: 'Diretrizes',
    pendingTasks: 'Tasks Pendentes',
    pendingExceptions: 'Exceções Pendentes',
    // KPI strip
    athletesLabel: 'Atletas',
    readinessLabel: 'Readiness (7d)',
    alertsLabel: 'Alertas',
    exceptionsLabel: 'Exceções',
    // Widgets
    directivesByCategoryTitle: 'Diretrizes por Categoria',
    directivesByCategorySubtitle: 'Foco, carga e alinhamento — plano ativo',
    viewAll: 'Ver todas',
    weeklyPerformanceTitle: 'Performance Semanal',
    weeklyPerformanceSubtitle: 'Readiness, carga e aderência — últimos 7 dias',
    weekColumn: 'Semana',
    totalLoadColumn: 'Carga Total',
    executionTasksTitle: 'Tarefas de Execução',
    executionTasksSubtitle: (n) => `${n} pendente${n !== 1 ? 's' : ''}`,
    allTasks: 'Todas',
    allTasksDone: 'Todas as tarefas concluídas',
    recentDecisionsTitle: 'Decisões Recentes',
    recentDecisionsSubtitle: 'Log do Gestor',
    pendingExceptionsTitle: 'Exceções Pendentes',
    pendingExceptionsSubtitle: (n) => `${n} aguardando decisão`,
    noPendingExceptions: 'Nenhuma exceção pendente',
    risksAlertsTitle: 'Riscos & Alertas',
    risksAlertsSubtitle: (n) => `${n} ativo${n !== 1 ? 's' : ''}`,
    noAlerts: 'Nenhum alerta ativo',
    noPlan: 'Nenhum plano ativo',
    noPlanDesc: 'Crie um plano estratégico com ajuda do agente inteligente',
    createStrategicPlan: 'Criar Plano Estratégico',
    pendingLabel: 'PENDENTE',
    ruleViolated: 'Regra:',
    requestedBy: 'Solicitado por',
    // Quick actions
    integratedPlan: 'Plano Integrado',
    staffView: 'Visão Staff',
    categories: 'Categorias',
    financial: 'Financeiro',
    support: 'Suporte',
  },
  en: {
    pageTitle: 'Strategic Manager',
    pageSubtitle: 'Macro planning · Category directives · Methodology governance',
    viewPlan: 'View Plan',
    active: 'ACTIVE',
    viewFullPlan: 'View Full Plan',
    macrocyclePhasesLabel: 'Macrocycle Phases',
    currentLabel: '← CURRENT',
    progressLabel: 'Progress',
    weekLabel: (elapsed, total) => `Wk ${elapsed}/${total}`,
    northStar: 'North Star',
    alignmentScore: 'Alignment Score',
    directives: 'Directives',
    pendingTasks: 'Pending Tasks',
    pendingExceptions: 'Pending Exceptions',
    athletesLabel: 'Athletes',
    readinessLabel: 'Readiness (7d)',
    alertsLabel: 'Alerts',
    exceptionsLabel: 'Exceptions',
    directivesByCategoryTitle: 'Category Directives',
    directivesByCategorySubtitle: 'Focus, load and alignment — active plan',
    viewAll: 'View all',
    weeklyPerformanceTitle: 'Weekly Performance',
    weeklyPerformanceSubtitle: 'Readiness, load and adherence — last 7 days',
    weekColumn: 'Week',
    totalLoadColumn: 'Total Load',
    executionTasksTitle: 'Execution Tasks',
    executionTasksSubtitle: (n) => `${n} pending`,
    allTasks: 'All',
    allTasksDone: 'All tasks completed',
    recentDecisionsTitle: 'Recent Decisions',
    recentDecisionsSubtitle: 'Manager log',
    pendingExceptionsTitle: 'Pending Exceptions',
    pendingExceptionsSubtitle: (n) => `${n} awaiting decision`,
    noPendingExceptions: 'No pending exceptions',
    risksAlertsTitle: 'Risks & Alerts',
    risksAlertsSubtitle: (n) => `${n} active`,
    noAlerts: 'No active alerts',
    noPlan: 'No active plan',
    noPlanDesc: 'Create a strategic plan with help from the intelligent agent',
    createStrategicPlan: 'Create Strategic Plan',
    pendingLabel: 'PENDING',
    ruleViolated: 'Rule:',
    requestedBy: 'Requested by',
    integratedPlan: 'Integrated Plan',
    staffView: 'Staff View',
    categories: 'Categories',
    financial: 'Financial',
    support: 'Support',
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// Reusable Components
// ═══════════════════════════════════════════════════════════════════════════════

const WidgetCard = ({ icon: Icon, title, subtitle, action, children, className = '', accent }) => (
  <div className={`bg-white rounded-2xl border overflow-hidden ${className}`} style={{ borderColor: accent || VUKZ.border }}>
    <div className="px-5 pt-5 pb-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: VUKZ_GRADIENT }}>
            <Icon size={18} color="white" />
          </div>
          <div>
            <h2 className="text-sm font-extrabold" style={{ color: VUKZ.text }}>{title}</h2>
            {subtitle && <p className="text-[10px]" style={{ color: VUKZ.textLight }}>{subtitle}</p>}
          </div>
        </div>
        {action}
      </div>
    </div>
    <div className="px-5 pb-5">{children}</div>
  </div>
);

const SkeletonRows = ({ count = 3 }) => (
  <div className="space-y-2">
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className="h-16 rounded-xl animate-pulse" style={{ backgroundColor: VUKZ.primaryLight }} />
    ))}
  </div>
);

const TrendIcon = ({ trend }) => {
  if (trend === 'up') return <ArrowUpRight size={14} style={{ color: VUKZ.success }} />;
  if (trend === 'down') return <ArrowDownRight size={14} style={{ color: VUKZ.error }} />;
  return <Minus size={14} style={{ color: VUKZ.textLight }} />;
};

const ReadinessBadge = ({ value }) => {
  const v = value ?? 0;
  const color = v >= 7.5 ? VUKZ.success : v >= 6.5 ? VUKZ.warning : VUKZ.error;
  return (
    <span className="text-xs font-black px-2 py-0.5 rounded-full" style={{ backgroundColor: `${color}15`, color }}>
      {v.toFixed(1)}
    </span>
  );
};

const AlignmentBar = ({ score }) => {
  const color = score >= 80 ? VUKZ.success : score >= 65 ? VUKZ.warning : VUKZ.error;
  return (
    <div className="flex items-center gap-2 w-full">
      <div className="flex-1 h-2 rounded-full" style={{ backgroundColor: VUKZ.primaryLight }}>
        <div className="h-full rounded-full transition-all" style={{ width: `${score}%`, backgroundColor: color }} />
      </div>
      <span className="text-[11px] font-bold min-w-[32px] text-right" style={{ color }}>{score}%</span>
    </div>
  );
};

const StatusBadge = ({ status, map }) => {
  const s = map[status] || { label: status, bg: '#f3f4f6', text: '#374151' };
  return (
    <span className="text-[9px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: s.bg, color: s.text }}>
      {s.label}
    </span>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Strategic Plan Banner — Plano Ativo
// ═══════════════════════════════════════════════════════════════════════════════

// Demo (F2): plano estratégico fora do escopo — esconde links/CTAs sem montar rota
const PLAN_IN_SCOPE = !isRouteOutOfScope('/manager/plan');

const ActivePlanBanner = ({ plan, T }) => {
  const today = new Date();
  const start = new Date(plan.period_start);
  const end = new Date(plan.period_end);
  const totalDays = Math.max(1, (end - start) / 86400000);
  const elapsedDays = Math.max(0, (today - start) / 86400000);
  const progressPct = Math.min(100, Math.round((elapsedDays / totalDays) * 100));

  const currentPhase = (plan.phases || []).find((p) => {
    const ps = new Date(p.start);
    const pe = new Date(p.end);
    return today >= ps && today <= pe;
  });

  const phaseStyle = currentPhase ? (PHASE_COLORS[currentPhase.name] || PHASE_COLORS.Base) : PHASE_COLORS.Base;

  return (
    <div className="bg-white rounded-2xl border overflow-hidden mb-6" style={{ borderColor: VUKZ.border }}>
      {/* Gradient top accent */}
      <div className="h-1.5" style={{ background: VUKZ_GRADIENT }} />

      <div className="p-5">
        {/* Title row */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: VUKZ_GRADIENT }}>
              <Compass size={22} color="white" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <h2 className="text-lg font-black" style={{ color: VUKZ.text }}>{plan.plan_title}</h2>
                <span className="text-[9px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: VUKZ.primaryLight, color: VUKZ.text }}>
                  {T.active}
                </span>
              </div>
              <p className="text-[11px]" style={{ color: VUKZ.textLight }}>
                v{plan.methodology_version} · {plan.duration_weeks} semanas · {new Date(plan.period_start).toLocaleDateString('pt-BR')} → {new Date(plan.period_end).toLocaleDateString('pt-BR')}
              </p>
            </div>
          </div>

          {PLAN_IN_SCOPE && (
            <Link
              to="/manager/plan"
              className="flex items-center gap-1.5 text-[11px] font-bold px-4 py-2 rounded-lg hover:opacity-80 transition-opacity"
              style={{ background: VUKZ_GRADIENT, color: 'white' }}
            >
              {T.viewFullPlan} <ChevronRight size={14} />
            </Link>
          )}
        </div>

        {/* North Star */}
        <div className="flex items-start gap-2 p-3 rounded-xl mb-4" style={{ backgroundColor: VUKZ.primaryLight }}>
          <Star size={14} className="mt-0.5 flex-shrink-0" style={{ color: VUKZ.textLight }} />
          <div>
            <span className="text-[9px] font-bold uppercase" style={{ color: VUKZ.textLight }}>{T.northStar}</span>
            <p className="text-[11px] font-semibold leading-relaxed" style={{ color: VUKZ.text }}>{plan.north_star}</p>
          </div>
        </div>

        {/* Phase Timeline + Progress */}
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Phase pills */}
          <div className="flex-1">
            <p className="text-[9px] font-bold uppercase mb-2" style={{ color: VUKZ.textLight }}>{T.macrocyclePhasesLabel}</p>
            <div className="flex flex-wrap gap-1.5">
              {(plan.phases || []).map((phase) => {
                const ps = PHASE_COLORS[phase.name] || PHASE_COLORS.Base;
                const isCurrent = currentPhase && currentPhase.name === phase.name;
                return (
                  <div
                    key={phase.name}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold ${isCurrent ? 'ring-2 ring-offset-1' : ''}`}
                    style={{ backgroundColor: ps.bg, color: ps.text, ringColor: isCurrent ? ps.dot : 'transparent' }}
                  >
                    <CircleDot size={10} style={{ color: ps.dot }} />
                    {phase.name}
                    {isCurrent && <span className="text-[8px] opacity-70">{T.currentLabel}</span>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Progress ring */}
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-full flex items-center justify-center relative" style={{ background: `conic-gradient(${VUKZ.primary} ${progressPct * 3.6}deg, ${VUKZ.primaryLight} 0)` }}>
              <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                <span className="text-xs font-black" style={{ color: VUKZ.text }}>{progressPct}%</span>
              </div>
            </div>
            <div>
              <div className="text-xs font-bold" style={{ color: VUKZ.text }}>{T.progressLabel}</div>
              <div className="text-[10px]" style={{ color: VUKZ.textLight }}>{T.weekLabel(Math.ceil(elapsedDays / 7), plan.duration_weeks)}</div>
            </div>
          </div>
        </div>

        {/* KPI pills */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
          <div className="text-center p-2.5 rounded-xl" style={{ backgroundColor: VUKZ.bg }}>
            <div className="text-lg font-black" style={{ color: VUKZ.primary }}>{plan.avg_alignment_score || '—'}</div>
            <div className="text-[9px]" style={{ color: VUKZ.textLight }}>{T.alignmentScore}</div>
          </div>
          <div className="text-center p-2.5 rounded-xl" style={{ backgroundColor: VUKZ.bg }}>
            <div className="text-lg font-black" style={{ color: VUKZ.primary }}>{plan.directive_count || 0}</div>
            <div className="text-[9px]" style={{ color: VUKZ.textLight }}>{T.directives}</div>
          </div>
          <div className="text-center p-2.5 rounded-xl" style={{ backgroundColor: VUKZ.bg }}>
            <div className="text-lg font-black" style={{ color: VUKZ.primary }}>{plan.tasks_pending || 0}</div>
            <div className="text-[9px]" style={{ color: VUKZ.textLight }}>{T.pendingTasks}</div>
          </div>
          <div className="text-center p-2.5 rounded-xl" style={{ backgroundColor: VUKZ.bg }}>
            <div className="text-lg font-black" style={{ color: VUKZ.primary }}>
              {plan.exceptions_pending || 0}
            </div>
            <div className="text-[9px]" style={{ color: VUKZ.textLight }}>{T.pendingExceptions}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Directive Card (Category Directive)
// ═══════════════════════════════════════════════════════════════════════════════

const DirectiveCard = ({ directive }) => {
  const ds = DIRECTIVE_STATUS[directive.directive_status] || DIRECTIVE_STATUS.draft;
  return (
    <Link
      to={`/manager/categories/${directive.category_id}`}
      className="block bg-white rounded-xl border p-4 hover:shadow-md transition-all duration-200 group"
      style={{ borderColor: VUKZ.border }}
    >
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="text-sm font-extrabold group-hover:opacity-80 transition-opacity" style={{ color: VUKZ.text }}>
            {directive.category_name}
          </h3>
          <p className="text-[10px]" style={{ color: VUKZ.textLight }}>{directive.age_range}</p>
        </div>
        <StatusBadge status={directive.directive_status} map={DIRECTIVE_STATUS} />
      </div>

      {/* Focus areas */}
      <div className="space-y-1 mb-3">
        <div className="flex items-start gap-1.5">
          <Target size={10} className="mt-0.5 flex-shrink-0" style={{ color: VUKZ.primary }} />
          <span className="text-[10px] line-clamp-1" style={{ color: VUKZ.text }}>{directive.focus_technical}</span>
        </div>
        <div className="flex items-start gap-1.5">
          <Activity size={10} className="mt-0.5 flex-shrink-0" style={{ color: VUKZ.textLight }} />
          <span className="text-[10px] line-clamp-1" style={{ color: VUKZ.text }}>{directive.focus_physical}</span>
        </div>
      </div>

      {/* Load range + intensity */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="text-center p-1.5 rounded-lg" style={{ backgroundColor: VUKZ.bg }}>
          <div className="text-xs font-bold" style={{ color: VUKZ.text }}>{directive.weekly_load_min_km}–{directive.weekly_load_max_km}</div>
          <div className="text-[8px]" style={{ color: VUKZ.textLight }}>km/sem</div>
        </div>
        <div className="text-center p-1.5 rounded-lg" style={{ backgroundColor: VUKZ.bg }}>
          <div className="text-xs font-bold" style={{ color: VUKZ.text }}>{directive.intensity_distribution?.aerobic || '—'}%</div>
          <div className="text-[8px]" style={{ color: VUKZ.textLight }}>Aeróbio</div>
        </div>
        <div className="text-center p-1.5 rounded-lg" style={{ backgroundColor: VUKZ.bg }}>
          <div className="text-xs font-bold" style={{ color: VUKZ.text }}>{directive.intensity_distribution?.speed || '—'}%</div>
          <div className="text-[8px]" style={{ color: VUKZ.textLight }}>Velocidade</div>
        </div>
      </div>

      {/* Alignment + indicators */}
      <div className="flex items-center justify-between">
        <div className="flex-1 mr-3">
          <AlignmentBar score={directive.avg_alignment || 0} />
        </div>
        <div className="flex items-center gap-2">
          {directive.pending_tasks > 0 && (
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: VUKZ.primaryLight, color: VUKZ.text }}>
              {directive.pending_tasks} task{directive.pending_tasks > 1 ? 's' : ''}
            </span>
          )}
          {directive.pending_exceptions > 0 && (
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#e5e7eb', color: '#374151' }}>
              {directive.pending_exceptions} exc
            </span>
          )}
          <ChevronRight size={14} style={{ color: VUKZ.textLight }} />
        </div>
      </div>
    </Link>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Main Page
// ═══════════════════════════════════════════════════════════════════════════════

const ManagerHomePage = () => {
  const { lang } = useSportsLang();
  const T = LABELS[lang] || LABELS.pt;
  // ClubId vem do ActiveContextContext (selecionado no header).
  // SEM fallback para 1 (tenant errado): fail-closed — sem clubId, não busca nada.
  const { clubId: activeClubId } = useActiveContext();
  const effectiveClubId = activeClubId || null;
  const [plan, setPlan] = useState(null);
  const [directives, setDirectives] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [methodology, setMethodology] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [trends, setTrends] = useState([]);
  const [managerKpis, setManagerKpis] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!effectiveClubId) {
        // fail-closed: sem clube resolvido, não consultar nada (evita vazar tenant 1)
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const [dashboard, cats, meth, al, tr, kpis] = await Promise.all([
          getGestorDashboard(effectiveClubId),       // antes: sem param → caía no default tenantId=1
          getCategoriesSummary(effectiveClubId),     // antes: sem param → institution_id hardcoded 1
          getMethodology(),
          getAlerts(),
          getTrends(),
          getManagerKpis(),
        ]);
        setPlan(dashboard);
        setCategories(cats);
        setMethodology(meth);
        setAlerts(al.filter((a) => !a.resolved));
        setTrends(tr);
        setManagerKpis(kpis);

        // Fetch plan-dependent data — só se houver plano real (sem fallback 1)
        const planId = dashboard?.plan_id || dashboard?.id || null;
        if (planId) {
          const [dirs, decs, tsks, excs] = await Promise.all([
            getPlanDirectives(planId),
            getPlanDecisions(planId),
            getPlanTasks(planId),
            getPlanExceptions(planId),
          ]);
          setDirectives(dirs);
          setDecisions(decs);
          setTasks(tsks);
          setExceptions(excs.filter((e) => e.status === 'pending'));
        }
      } catch (err) {
        console.error('Error loading manager home:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [effectiveClubId]);

  const pendingTasks = useMemo(() => (tasks || []).filter((t) => t.status === 'pending'), [tasks]);
  const totalAthletes = (categories || []).reduce((s, c) => s + (c.athlete_count || 0), 0)
    || managerKpis?.availability?.total_athletes || 0;
  const avgReadiness = (categories || []).length
    ? ((categories || []).reduce((s, c) => s + (c.readiness_avg || 0), 0) / categories.length).toFixed(1)
    : '—';

  return (
    <PageLayout
      header={{
        title: T.pageTitle,
        subtitle: T.pageSubtitle,
        actions: PLAN_IN_SCOPE ? (
          <Link
            to="/manager/plan"
            className="rounded-lg px-3 py-2 text-sm font-medium text-white transition-colors hover:opacity-90"
            style={{ backgroundColor: VUKZ.primary }}
          >
            {T.viewPlan}
          </Link>
        ) : null,
      }}
    >        {/* ── Quota Suspension Alert (só aparece se houver atletas suspensos por quota) ── */}
        <QuotaSuspensionAlert />
        {/* ── Active Plan Banner ───────────────────────────────── */}
        {loading ? (
          <div className="h-52 rounded-2xl animate-pulse mb-6" style={{ backgroundColor: VUKZ.primaryLight }} />
        ) : plan ? (
          <ActivePlanBanner plan={plan} T={T} />
        ) : (
          <div className="bg-white rounded-2xl border p-8 text-center mb-6" style={{ borderColor: VUKZ.border }}>
            <Compass size={32} className="mx-auto mb-3" style={{ color: VUKZ.textLight }} />
            <h3 className="text-sm font-bold mb-1" style={{ color: VUKZ.text }}>{T.noPlan}</h3>
            <p className="text-[11px] mb-4" style={{ color: VUKZ.textLight }}>{T.noPlanDesc}</p>
            {PLAN_IN_SCOPE && (
              <Link
                to="/manager/strategic-plan"
                className="inline-flex items-center gap-1.5 text-[11px] font-bold px-4 py-2 rounded-lg"
                style={{ background: VUKZ_GRADIENT, color: 'white' }}
              >
                <Zap size={14} /> {T.createStrategicPlan}
              </Link>
            )}
          </div>
        )}

        {/* ── KPI Strip ────────────────────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          {[
                      { label: T.athletesLabel, value: totalAthletes, icon: Users, color: VUKZ.primary },
            { label: T.readinessLabel, value: avgReadiness, icon: TrendingUp, color: VUKZ.success },
            { label: T.alignmentScore, value: plan?.avg_alignment_score || '—', icon: Target, color: VUKZ.primary },
            { label: T.alertsLabel, value: alerts.length, icon: AlertTriangle, color: alerts.length > 0 ? VUKZ.text : VUKZ.textLight },
            { label: T.exceptionsLabel, value: exceptions.length, icon: Scale, color: exceptions.length > 0 ? VUKZ.text : VUKZ.textLight },
          ].map((kpi) => (
            <div key={kpi.label} className="bg-white rounded-xl border p-3 flex items-center gap-3" style={{ borderColor: VUKZ.border }}>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${kpi.color}10` }}>
                <kpi.icon size={16} style={{ color: kpi.color }} />
              </div>
              <div>
                <div className="text-lg font-black" style={{ color: VUKZ.text }}>{kpi.value}</div>
                <div className="text-[10px]" style={{ color: VUKZ.textLight }}>{kpi.label}</div>
              </div>
            </div>
          ))}
        </div>

        {loading ? (
          <SkeletonRows count={5} />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* ════════ LEFT COLUMN (2/3) ════════ */}
            <div className="lg:col-span-2 space-y-6">

              {/* Widget 1: Diretrizes por Categoria */}
              <WidgetCard
                icon={Layers}
                title={T.directivesByCategoryTitle}
                subtitle={T.directivesByCategorySubtitle}
                action={PLAN_IN_SCOPE && (
                  <Link to="/manager/plan" className="text-[10px] font-bold px-2.5 py-1 rounded-lg hover:opacity-80 transition-opacity" style={{ color: VUKZ.primary, backgroundColor: VUKZ.primaryLight }}>
                    {T.viewAll}
                  </Link>
                )}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {directives.map((dir) => (
                    <DirectiveCard key={dir.id} directive={dir} />
                  ))}
                </div>
              </WidgetCard>

              {/* Widget 2: Performance por Categoria (legacy) */}
              <WidgetCard icon={BarChart3} title={T.weeklyPerformanceTitle} subtitle={T.weeklyPerformanceSubtitle}>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr style={{ color: VUKZ.textLight }}>
                        <th className="text-left py-2 font-semibold">{T.weekColumn}</th>
                        {categories.map((c) => (
                          <th key={c.id} className="text-center py-2 font-semibold">{c.name}</th>
                        ))}
                        <th className="text-right py-2 font-semibold">{T.totalLoadColumn}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trends.map((t) => (
                        <tr key={t.week} className="border-t" style={{ borderColor: VUKZ.border }}>
                          <td className="py-2.5 font-bold" style={{ color: VUKZ.text }}>{t.week}</td>
                          {['infantil', 'juvenil', 'junior', 'senior', 'master'].map((key) => (
                            <td key={key} className="text-center py-2.5">
                              <ReadinessBadge value={t[key] ?? 0} />
                            </td>
                          ))}
                          <td className="text-right py-2.5 font-bold" style={{ color: VUKZ.text }}>
                            {t.load_total} km
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </WidgetCard>
            </div>

            {/* ════════ RIGHT COLUMN (1/3) ════════ */}
            <div className="space-y-6">

              {/* C3 — Consumo de IA do Clube (gestor) */}
              <UsageWidget
                scope="club"
                fetcher={getClubUsage}
                detailHref="/meu-consumo/club"
              />

              {/* VUKZ Assist Panel (Performance Manager Agent) */}
              <VukzAssistPanel role="GESTOR" sport="swimming" />

              {/* Widget 3: Execution Tasks (Pending) */}
              <WidgetCard
                icon={ListTodo}
                title={T.executionTasksTitle}
                subtitle={T.executionTasksSubtitle(pendingTasks.length)}
                action={PLAN_IN_SCOPE && (
                  <Link to="/manager/plan" className="text-[10px] font-bold px-2.5 py-1 rounded-lg hover:opacity-80 transition-opacity" style={{ color: VUKZ.primary, backgroundColor: VUKZ.primaryLight }}>
                    {T.allTasks}
                  </Link>
                )}
              >
                {pendingTasks.length === 0 ? (
                  <div className="text-center py-6">
                    <CheckCircle2 size={20} className="mx-auto mb-2" style={{ color: VUKZ.success }} />
                    <p className="text-[11px] font-medium" style={{ color: VUKZ.textLight }}>{T.allTasksDone}</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {pendingTasks.slice(0, 5).map((task) => (
                      <div key={task.id} className="flex items-start gap-2 p-2.5 rounded-lg border" style={{ borderColor: VUKZ.border }}>
                        <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: VUKZ.primaryLight }}>
                          <Zap size={12} style={{ color: VUKZ.text }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] font-bold leading-snug" style={{ color: VUKZ.text }}>{task.title}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#e5e7eb', color: '#374151' }}>
                              {AGENT_LABELS[task.owner_agent] || task.owner_agent}
                            </span>
                            {task.due_date && (
                              <span className="text-[9px] flex items-center gap-0.5" style={{ color: VUKZ.textLight }}>
                                <Calendar size={8} /> {new Date(task.due_date).toLocaleDateString('pt-BR')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </WidgetCard>

              {/* Widget 4: Decision Log (últimas decisões) */}
              <WidgetCard
                icon={Gavel}
                title={T.recentDecisionsTitle}
                subtitle={T.recentDecisionsSubtitle}
                action={PLAN_IN_SCOPE && (
                  <Link to="/manager/plan" className="text-[10px] font-bold px-2.5 py-1 rounded-lg hover:opacity-80 transition-opacity" style={{ color: VUKZ.primary, backgroundColor: VUKZ.primaryLight }}>
                    {T.viewAll}
                  </Link>
                )}
              >
                <div className="space-y-2">
                  {decisions.slice(0, 4).map((dec) => {
                    const dStyle = DECISION_TYPE_COLORS[dec.decision_type] || DECISION_TYPE_COLORS.strategic;
                    return (
                      <div key={dec.id} className="p-2.5 rounded-lg" style={{ backgroundColor: `${dStyle.bg}40` }}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: dStyle.bg, color: dStyle.text }}>
                            {DECISION_TYPE_LABELS[dec.decision_type]}
                          </span>
                          {dec.category_id && (
                            <span className="text-[9px]" style={{ color: VUKZ.textLight }}>· {dec.category_id}</span>
                          )}
                        </div>
                        <p className="text-[10px] font-bold leading-snug" style={{ color: VUKZ.text }}>{dec.title}</p>
                        <p className="text-[9px] mt-0.5 line-clamp-2" style={{ color: VUKZ.textLight }}>{dec.rationale}</p>
                      </div>
                    );
                  })}
                </div>
              </WidgetCard>

              {/* Widget 5: Exceções Pendentes */}
              <WidgetCard
                icon={Scale}
                title={T.pendingExceptionsTitle}
                subtitle={T.pendingExceptionsSubtitle(exceptions.length)}
                accent={exceptions.length > 0 ? '#d1d5db' : undefined}
              >
                {exceptions.length === 0 ? (
                  <div className="text-center py-6">
                    <CheckCircle2 size={20} className="mx-auto mb-2" style={{ color: VUKZ.success }} />
                    <p className="text-[11px] font-medium" style={{ color: VUKZ.textLight }}>{T.noPendingExceptions}</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {exceptions.map((exc) => (
                      <div key={exc.id} className="p-3 rounded-lg border" style={{ borderColor: '#e5e7eb', backgroundColor: VUKZ.primaryLight }}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#e5e7eb', color: '#374151' }}>
                            {exc.category_id}
                          </span>
                          <span className="text-[9px] font-bold" style={{ color: VUKZ.text }}>{T.pendingLabel}</span>
                        </div>
                        <p className="text-[10px] font-bold mb-1" style={{ color: VUKZ.text }}>
                          {T.ruleViolated} {exc.rule_violated}
                        </p>
                        <p className="text-[9px] leading-relaxed" style={{ color: VUKZ.textLight }}>
                          {exc.reason}
                        </p>
                        <p className="text-[9px] mt-1" style={{ color: VUKZ.textLight }}>
                          {T.requestedBy} {exc.requested_by}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </WidgetCard>

              {/* Widget 6: Riscos & Alertas */}
              <WidgetCard icon={AlertTriangle} title={T.risksAlertsTitle} subtitle={T.risksAlertsSubtitle(alerts.length)}>
                {alerts.length === 0 ? (
                  <div className="text-center py-6">
                    <CheckCircle2 size={20} className="mx-auto mb-2" style={{ color: VUKZ.success }} />
                    <p className="text-[11px] font-medium" style={{ color: VUKZ.textLight }}>{T.noAlerts}</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {alerts.slice(0, 5).map((alert) => {
                      const sev = SEVERITY_COLORS[alert.severity] || { bg: '#f3f4f6', text: '#374151', dot: '#9ca3af' };
                      return (
                        <div key={alert.id} className="flex items-start gap-2 p-2.5 rounded-lg" style={{ backgroundColor: sev.bg }}>
                          <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: sev.dot }} />
                          <div className="flex-1 min-w-0">
                            <p className="text-[10px] font-semibold" style={{ color: sev.text }}>{alert.category}</p>
                            <p className="text-[10px] leading-relaxed" style={{ color: sev.text }}>{alert.message}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </WidgetCard>
            </div>
          </div>
        )}

        {/* ── Quick Actions ────────────────────────────────────── */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-5 gap-3">
          {[
            // '/manager/plan' retirado do escopo da demo (config/demoScope.js)
            { label: T.staffView, icon: BarChart3, to: '/staff/planning' },
            { label: T.categories, icon: Layers, to: '/manager/categories' },
            { label: T.financial, icon: Shield, to: '/manager/financial' },
            { label: T.support, icon: Layers, to: '/manager/support' },
          ].map((action) => (
            <Link
              key={action.label}
              to={action.to}
              className="flex items-center gap-3 bg-white rounded-xl border p-4 hover:shadow-md transition-all group"
              style={{ borderColor: VUKZ.border }}
            >
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: VUKZ_GRADIENT }}>
                <action.icon size={16} color="white" />
              </div>
              <span className="text-sm font-bold group-hover:opacity-80 transition-opacity" style={{ color: VUKZ.text }}>
                {action.label}
              </span>
              <ChevronRight size={16} className="ml-auto" style={{ color: VUKZ.textLight }} />
            </Link>
          ))}
        </div>
    </PageLayout>
  );
};

export default ManagerHomePage;
